import React, { useState, useEffect } from "react";
import { 
  X, 
  CheckSquare, 
  Square, 
  CheckCircle2, 
  Clock, 
  Plus, 
  Trash2, 
  User, 
  Calendar, 
  Filter,
  Loader2
} from "lucide-react";
import { useTasks } from "../../hooks/useTasks";
import { useAuth } from "../../context/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

export default function POTasksModal({ isOpen, po, onClose }) {
  const { tasks, addTask, toggleTaskComplete, deleteTask } = useTasks();
  const { currentUser } = useAuth();

  const [filterStatus, setFilterStatus] = useState("incomplete"); // 'incomplete' | 'completed' | 'all'
  const [users, setUsers] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [newTaskAssignee, setNewTaskAssignee] = useState("");
  const [error, setError] = useState("");

  // Load team users for assignment dropdown
  useEffect(() => {
    async function loadUsers() {
      try {
        const snap = await getDocs(collection(db, "users"));
        const list = [];
        snap.forEach((d) => {
          const u = d.data();
          if (!u.deleted && u.status !== "Disabled") {
            list.push({
              id: d.id,
              name: u.fullName || u.name || u.email || "User",
              email: u.email
            });
          }
        });
        setUsers(list);
      } catch (err) {
        console.error("Error loading users for POTasksModal:", err);
      }
    }
    if (isOpen) {
      loadUsers();
      setFilterStatus("incomplete");
      setIsAdding(false);
      setError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentUser) {
      setNewTaskAssignee(currentUser.displayName || currentUser.email || "");
    }
  }, [isOpen, currentUser]);

  if (!isOpen || !po) return null;

  // Filter tasks specific to this Estimate/PO
  const poTasks = tasks.filter((t) => {
    const matchesId = (t.estimateId && po.id && t.estimateId === po.id) || (t.poId && po.id && t.poId === po.id);
    const matchesNumber = (t.estimateNumber && po.poNumber && t.estimateNumber === po.poNumber) || (t.poNumber && po.poNumber && t.poNumber === po.poNumber);
    return matchesId || matchesNumber;
  });

  // Apply completion status filter
  const filteredTasks = poTasks.filter((t) => {
    if (filterStatus === "incomplete") return !t.completed;
    if (filterStatus === "completed") return t.completed;
    return true; // 'all'
  });

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) {
      setError("Task title is required.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      const taskData = {
        title: newTaskTitle.trim(),
        type: "custom",
        completed: false,
        dueDate: newTaskDueDate || "",
        assignedTo: newTaskAssignee || currentUser?.displayName || currentUser?.email || "Unassigned",
        estimateId: po.id || "",
        estimateNumber: po.poNumber || "",
        poId: po.id || "",
        poNumber: po.poNumber || "",
        estimateName: `${po.poNumber || "Estimate"}${po.clientName ? ` - ${po.clientName}` : ""}`,
        createdBy: currentUser?.email || "System",
        createdAt: new Date().toISOString()
      };

      await addTask(taskData);
      setNewTaskTitle("");
      setNewTaskDueDate("");
      setIsAdding(false);
    } catch (err) {
      console.error("Error creating task for PO:", err);
      setError("Failed to create task. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (task) => {
    try {
      const completer = currentUser?.displayName || currentUser?.email || "User";
      await toggleTaskComplete(task.id, task.completed, completer);
    } catch (err) {
      console.error("Error toggling task completion:", err);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await deleteTask(taskId);
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem"
      }}
      onClick={onClose}
    >
      <div
        className="card glass-card"
        style={{
          width: "720px",
          maxWidth: "100%",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          padding: 0,
          overflow: "hidden"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.02)"
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "var(--primary)", display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1.15rem" }}>
              <CheckSquare size={20} /> Tasks & Action Items
            </h3>
            <p style={{ margin: "0.25rem 0 0 0", color: "var(--text-secondary)", fontSize: "0.825rem" }}>
              EST: <strong style={{ color: "var(--text-primary)" }}>{po.poNumber || "N/A"}</strong>
              {po.clientName ? ` — Client: ${po.clientName}` : ""}
              {po.customerPoNumber ? ` (Cust PO: ${po.customerPoNumber})` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              padding: "0.25rem",
              display: "flex",
              alignItems: "center"
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Toolbar & Filter Controls */}
        <div
          style={{
            padding: "0.85rem 1.5rem",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            flexWrap: "wrap",
            gap: "0.75rem"
          }}
        >
          {/* Segmented Filter */}
          <div style={{ display: "inline-flex", borderRadius: "6px", backgroundColor: "var(--bg-main)", padding: "2px", border: "1px solid var(--border-color)" }}>
            <button
              type="button"
              onClick={() => setFilterStatus("incomplete")}
              style={{
                background: filterStatus === "incomplete" ? "var(--primary)" : "transparent",
                color: filterStatus === "incomplete" ? "#000000" : "var(--text-secondary)",
                border: "none",
                borderRadius: "4px",
                padding: "0.35rem 0.75rem",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              Non-Completed ({poTasks.filter(t => !t.completed).length})
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus("completed")}
              style={{
                background: filterStatus === "completed" ? "var(--primary)" : "transparent",
                color: filterStatus === "completed" ? "#000000" : "var(--text-secondary)",
                border: "none",
                borderRadius: "4px",
                padding: "0.35rem 0.75rem",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              Completed ({poTasks.filter(t => t.completed).length})
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus("all")}
              style={{
                background: filterStatus === "all" ? "var(--primary)" : "transparent",
                color: filterStatus === "all" ? "#000000" : "var(--text-secondary)",
                border: "none",
                borderRadius: "4px",
                padding: "0.35rem 0.75rem",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              All ({poTasks.length})
            </button>
          </div>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setIsAdding(!isAdding)}
            style={{ display: "flex", alignItems: "center", gap: "0.35rem", padding: "0.35rem 0.75rem" }}
          >
            <Plus size={14} /> {isAdding ? "Cancel" : "Add Task"}
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem"
          }}
        >
          {error && (
            <div className="alert alert-danger" style={{ margin: 0, padding: "0.6rem 0.8rem", fontSize: "0.85rem" }}>
              {error}
            </div>
          )}

          {/* New Task Inline Form */}
          {isAdding && (
            <form
              onSubmit={handleCreateTask}
              style={{
                padding: "1rem",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid var(--primary)",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                marginBottom: "0.5rem"
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--primary)" }}>
                Create New Task for this Estimate
              </div>

              <input
                type="text"
                placeholder="What needs to be done? (e.g. Verify vendor lead time, Confirm freight rate...)"
                className="form-input"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                autoFocus
                required
                style={{ margin: 0 }}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.75rem", alignItems: "center" }}>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>
                    Assignee
                  </label>
                  <select
                    className="form-input"
                    value={newTaskAssignee}
                    onChange={(e) => setNewTaskAssignee(e.target.value)}
                    style={{ margin: 0, height: "36px", padding: "0.2rem 0.5rem", fontSize: "0.85rem" }}
                  >
                    {users.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                    {users.length === 0 && (
                      <option value={currentUser?.displayName || currentUser?.email || "Me"}>
                        {currentUser?.displayName || currentUser?.email || "Me"}
                      </option>
                    )}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={newTaskDueDate}
                    onChange={(e) => setNewTaskDueDate(e.target.value)}
                    style={{ margin: 0, height: "36px", padding: "0.2rem 0.5rem", fontSize: "0.85rem" }}
                  />
                </div>

                <div style={{ alignSelf: "flex-end" }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSaving}
                    style={{ height: "36px", display: "flex", alignItems: "center", gap: "0.35rem", padding: "0 1rem" }}
                  >
                    {isSaving ? <Loader2 size={14} className="spinner-icon" /> : "Save Task"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Tasks Listing */}
          {filteredTasks.length === 0 ? (
            <div style={{ padding: "3rem 1rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <CheckCircle2 size={40} style={{ margin: "0 auto 1rem auto", opacity: 0.3, color: "var(--primary)" }} />
              <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-primary)", marginBottom: "0.25rem" }}>
                {filterStatus === "incomplete" ? "No Active Tasks" : filterStatus === "completed" ? "No Completed Tasks" : "No Tasks Found"}
              </div>
              <p style={{ margin: 0, fontSize: "0.85rem", fontStyle: "italic" }}>
                {filterStatus === "incomplete" 
                  ? "All tasks for this estimate have been completed, or none have been assigned yet." 
                  : "Click 'Add Task' above to create action items for this estimate."}
              </p>
            </div>
          ) : (
            filteredTasks.map((task) => {
              const completedDateStr = task.completedAt
                ? new Date(task.completedAt).toLocaleDateString()
                : "";

              return (
                <div
                  key={task.id}
                  style={{
                    padding: "0.85rem 1rem",
                    borderRadius: "8px",
                    backgroundColor: task.completed ? "rgba(255, 255, 255, 0.015)" : "rgba(255, 255, 255, 0.04)",
                    border: "1px solid var(--border-color)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.4rem",
                    opacity: task.completed ? 0.75 : 1,
                    transition: "all 0.15s"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flex: 1, minWidth: 0 }}>
                      <button
                        type="button"
                        onClick={() => handleToggle(task)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          color: task.completed ? "#10B981" : "var(--text-muted)"
                        }}
                        title={task.completed ? "Mark incomplete" : "Mark complete"}
                      >
                        {task.completed ? <CheckSquare size={18} color="#10B981" /> : <Square size={18} />}
                      </button>
                      <span
                        style={{
                          fontSize: "0.925rem",
                          fontWeight: 600,
                          color: task.completed ? "var(--text-muted)" : "var(--text-primary)",
                          textDecoration: task.completed ? "line-through" : "none"
                        }}
                      >
                        {task.title}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDelete(task.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--text-muted)",
                        cursor: "pointer",
                        padding: "2px",
                        display: "flex",
                        alignItems: "center"
                      }}
                      title="Delete task"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.85rem",
                      fontSize: "0.775rem",
                      color: "var(--text-secondary)",
                      paddingTop: "0.25rem",
                      marginLeft: "1.7rem"
                    }}
                  >
                    {task.assignedTo && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                        <User size={12} />
                        <span>Assigned: <strong style={{ color: "var(--text-primary)" }}>{task.assignedTo}</strong></span>
                      </span>
                    )}

                    {task.dueDate && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                        <Calendar size={12} />
                        <span>Due: <strong style={{ color: "var(--text-primary)" }}>{task.dueDate}</strong></span>
                      </span>
                    )}

                    {task.completed && task.completedBy && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", color: "#10B981" }}>
                        <CheckCircle2 size={12} />
                        <span>Completed by <strong>{task.completedBy}</strong>{completedDateStr ? ` on ${completedDateStr}` : ""}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "0.85rem 1.5rem",
            borderTop: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "rgba(255, 255, 255, 0.01)"
          }}
        >
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
